import TitleBar from '../../../../components/TitleBar';
import React, { useEffect, useState } from 'react';
import { Footer } from '../../../../components/common/Footer';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  BorderedSection,
  Button,
  FilterHeader,
  Loading,
  SaveInstructions,
} from '../../../../components';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CREATE_DEPENDENCY_MATRIX,
  FETCH_LEVEL_ZERO_LOCATIONS,
  UNCHECK_LOCATION,
  VALIDATE_UNCHECK_LOCATION,
} from '../../queries';
import { useGetQueries } from '../../../../utils/useGetQueries';
import { LocationsSelection } from '../DependencyMatrix';
import { message } from 'antd';
import ListManager from '../../../../components/common/ListManager';
import { useUserContext } from '../../../../context/PermissionsContext';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import { ASSESS_INTERMEDIATE_DEPENDENCIES } from '../../queries/FinishedGoodsMatrix';
import {
  Location,
  sortLocationsByName,
  filterLocationsBySearch,
  showUncheckLocationError,
} from './utils';
import { RemoveLocationsModal } from './components/RemoveLocationsModal';

type ValidationResponse = {
  validateUnCheckedLocation: boolean;
};

type LocationToBeRemoved = {
  uuid: string;
};

type AssessedIntermediateDependencies = {
  totalCount: number;
  hasDependencies: boolean;
  directDependenciesCount: number;
  indirectDependenciesCount: number;
  sampleDependencies: Location[];
  parentLocationMapping: {
    location: Location;
    dependencyId: string;
  }[];
};

function FinishedGoodsLocations() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const divisionId = params.divisionId!;
  const divisionName = searchParams.get('divisionName') || '';

  const [addedLocations, setAddedLocations] = useState<Location[]>([]);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [search, setSearch] = useState('');

  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [
    assessedIntermediateDependencies,
    setAssessedIntermediateDependencies,
  ] = useState<AssessedIntermediateDependencies>();

  const [locationsToBeRemoved, setLocationsToBeRemoved] = useState<
    LocationToBeRemoved[]
  >([]);

  const { canEdit } = useUserContext();

  const orderedAvailableLocations = sortLocationsByName(filteredLocations);
  const orderedAddedLocations = sortLocationsByName(addedLocations);

  const {
    data,
    loading,
    error,
    refetch: refetchLevelZeroLocations,
  } = useQuery<LocationsSelection>(FETCH_LEVEL_ZERO_LOCATIONS, {
    variables: {
      division: { uuid: divisionId },
      level: 0,
    },
  });

  const [assessIntermediateDependencies] = useLazyQuery(
    ASSESS_INTERMEDIATE_DEPENDENCIES,
    {
      variables: {
        input: {
          division: { uuid: divisionId },
        },
      },
    },
  );

  const [
    validateUncheck,
    { error: uncheckedError, loading: uncheckedLoading },
  ] = useLazyQuery<ValidationResponse>(VALIDATE_UNCHECK_LOCATION);

  // This code uses a custom hook `useGetQueries` to determine which GraphQL queries need to be refetched.
  // It passes an array of query names: 'GetMapping', 'DivisionSelectionDashboard', and 'fetchLevelLocations'
  // to the hook, which returns a list of queries that match these names and should be refetched.
  const refetchQueries = useGetQueries([
    'GetMapping',
    'DivisionSelectionDashboard',
    'fetchLevelLocations',
  ]);

  const [CreateDependencyMatrix, { loading: createDependencyLoading }] =
    useMutation(CREATE_DEPENDENCY_MATRIX);

  const [uncheckLocation, { loading: uncheckDependencyLoading }] = useMutation(
    UNCHECK_LOCATION,
    {
      refetchQueries,
    },
  );

  const checkedLocations = data?.findLocationsDependencyLevel.locationAux || [];

  const uncheckedLocations =
    data?.findLocationsDependencyLevel.unselectedLocations || [];

  // This is a hack to force the component to re-render when the data changes.
  const [forceUpdate, setForceUpdate] = useState(0);

  useEffect(() => {
    if (data) {
      setAddedLocations(checkedLocations);
      setAvailableLocations(uncheckedLocations);
      setFilteredLocations(uncheckedLocations);
    }
  }, [data, forceUpdate]);

  const onAddItem = (item: Location) => {
    setAddedLocations((prev) => [...prev, item]);
    setAvailableLocations((prev) =>
      prev.filter((loc) => loc.uuid !== item.uuid),
    );
    setFilteredLocations((prev) =>
      prev.filter((loc) => loc.uuid !== item.uuid),
    );
  };

  const onRemoveItem = async (item: Location) => {
    try {
      const response = await validateUncheck({
        variables: {
          input: {
            locations: [{ uuid: item.uuid }],
            division: { uuid: divisionId },
            location: { uuid: item.uuid },
            parentLocation: { uuid: item.uuid },
            level: 0,
          },
        },
      });

      if (response?.data?.validateUnCheckedLocation) {
        setAvailableLocations((prev) => [...prev, item]);
        setFilteredLocations((prev) => [...prev, item]);
        setAddedLocations((prev) =>
          prev.filter((loc) => loc.uuid !== item.uuid),
        );
        return;
      }

      showUncheckLocationError('top');
    } catch (e) {
      message.error('Failed to uncheck location');
    }
  };

  const cleanup = () => {
    // First close the modal since it's the UI element the user is interacting with
    setOpenRemoveModal(false);

    // Then clear the temporary states that were used for the modal
    setLocationsToBeRemoved([]);
    setAssessedIntermediateDependencies(undefined);

    // Then refresh the data from the server
    refetchLevelZeroLocations({
      division: { uuid: divisionId },
      level: 0,
    });

    // Finally force a rerender to ensure all changes are reflected
    setForceUpdate((prev) => prev + 1);
  };

  const handleAddLocations = async (locationsToAdd: { uuid: string }[]) => {
    if (locationsToAdd.length === 0) return;

    try {
      await CreateDependencyMatrix({
        variables: {
          input: {
            division: { uuid: divisionId },
            level: 0,
            locations: locationsToAdd,
          },
        },
      });
      message.success('Locations added successfully');
    } catch (error) {
      message.error('Failed to add locations');
      throw error;
    }
  };

  const handleRemoveLocations = async (
    locationsToRemove: { uuid: string }[],
  ) => {
    if (locationsToRemove.length === 0) return;

    try {
      const response = await assessIntermediateDependencies({
        variables: {
          input: {
            locations: locationsToRemove,
            division: { uuid: divisionId },
          },
        },
      });

      const assessedDependencies =
        response?.data?.assessIntermediateDependencies;

      if (assessedDependencies.totalCount > 0) {
        setAssessedIntermediateDependencies(assessedDependencies);
        setLocationsToBeRemoved(locationsToRemove);
        setOpenRemoveModal(true);
        return;
      }

      await uncheckLocation({
        variables: {
          input: {
            locations: locationsToRemove,
            division: { uuid: divisionId },
            level: 0,
          },
        },
      });
      message.success('Locations removed successfully');
    } catch (error) {
      message.error('Failed to remove locations');
      throw error;
    }
  };

  const onSaveChanges = async () => {
    if (!canEdit) {
      message.info('You do not have permission to edit this data');
      return;
    }

    try {
      const currentAddedLocations = [...addedLocations];
      const currentAvailableLocations = [...availableLocations];

      // Handle adding new locations
      const locationsToAdd = currentAddedLocations
        .filter(
          (loc) => !checkedLocations.some((cloc) => cloc.uuid === loc.uuid),
        )
        .map((loc) => ({ uuid: loc.uuid }));

      if (locationsToAdd.length > 0) {
        await handleAddLocations(locationsToAdd);
        // After successfully adding locations, refetch to update our reference state
        await refetchLevelZeroLocations({
          division: { uuid: divisionId },
          level: 0,
        });
      }

      // Handle removing locations
      const locationsToRemove = currentAvailableLocations
        .filter(
          (loc) => !uncheckedLocations.some((uloc) => uloc.uuid === loc.uuid),
        )
        .map((loc) => ({ uuid: loc.uuid }));

      if (locationsToRemove.length > 0) {
        await handleRemoveLocations(locationsToRemove);
        // After successfully removing locations, refetch to update our reference state
        await refetchLevelZeroLocations({
          division: { uuid: divisionId },
          level: 0,
        });
      }
    } catch (error) {
      // Error is already handled in the individual functions
      console.error('Failed to save changes:', error);
    }
  };

  const filterAvailableLocations = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    setFilteredLocations(filterLocationsBySearch(availableLocations, value));
  };

  let content;

  if (loading) {
    content = <Loading />;
  } else {
    content = (
      <ListManager<Location>
        addedItems={orderedAddedLocations}
        availableItems={orderedAvailableLocations}
        addedTitle="Locations Added"
        availableTitle="Locations Available"
        onAddItem={onAddItem}
        onRemoveItem={onRemoveItem}
        getItemLabel={(item: Location) => item.locationName}
        infoContent={<SaveInstructions isLocation={true} />}
      />
    );
  }

  if (uncheckedError) return <>Error! {uncheckedError.message}</>;

  if (error) return <>Error! {error.message}</>;

  return (
    <div>
      <TitleUpdater title={`${divisionName} Location Selection`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`Finished Goods Locations // ${divisionName}`}
        brief='Select which locations directly support finished products sold to external customers. Only
                      select locations that "touch" the product last. Exact percentages will be captured on the next
                      page.'
      />

      <BorderedSection
        noPadding
        headingClassName="h-20"
        contentClassName="py-4"
        extendOptions={
          <FilterHeader
            searchTerm={search}
            onSearch={filterAvailableLocations}
          />
        }
      >
        {content}
      </BorderedSection>
      <div className="flex items-center justify-center mt-4">
        <Button
          onClick={onSaveChanges}
          type="primary"
          className="text-sm mt-4 min-w-[180px]"
          disabled={
            uncheckedLoading ||
            createDependencyLoading ||
            uncheckDependencyLoading ||
            !canEdit
          }
        >
          Save
        </Button>
      </div>
      <RemoveLocationsModal
        openModal={openRemoveModal}
        onCancel={() => setOpenRemoveModal(false)}
        divisionId={divisionId}
        locationsToBeRemoved={locationsToBeRemoved}
        assessedIntermediateDependencies={assessedIntermediateDependencies}
        onCleanup={cleanup}
        onUncheckLocation={uncheckLocation}
      />
      <Footer
        backPath={'/dependency-mapping/division-selection'}
        nextPath={`/dependency-mapping/division-selection/${params.divisionId}/finished-goods-locations/finished-goods-mapping?level=0&divisionName=${divisionName}`}
      />
    </div>
  );
}

export default FinishedGoodsLocations;
